import {
  errorMessage,
  intlLocale,
  localeDateTimeFormat,
  moduleLabel,
  moduleTypeLabel,
  t,
  type ModuleId,
} from '@ctcells/i18n'

/**
 * 화면 표기 포맷 — 숫자·시각·온도·장치 이름.
 *
 * 🔴 **화면이 시간을 계산하지 않는다.** 여기 있는 것은 서버가 준 값을 *읽는 꼴로 바꾸는* 일뿐이다.
 *    경과·잔여·ETA 처럼 "지금"이 들어가는 값은 서버·구독이 준 표기를 그대로 쓴다(DP-04).
 */

/**
 * 표시 시간대. 장비가 놓인 현장 기준이다.
 * ⚠ 브라우저 로컬 시간대를 쓰면 같은 원장이 단말마다 다르게 보인다 — 감사 대조가 깨진다.
 * 🔸 배포 설정으로 뺄 값이다. 지금은 단일 상수로 두고 여기 한 줄만 바꾸면 되게 해 둔다.
 */
export const DISPLAY_TIME_ZONE = 'Asia/Seoul'

/*
 * 🔴 **표기 꼴은 언어와 상관없이 같다**(`14:31` · `09-11 14:31` · `2026-09-30 13:05:42`) — 표 폭이 흔들리지 않게 조각으로 짓고,
 *    오전/오후 · 12시간제가 끼지 않게 시 자리를 `h23` 으로 못박는다(`en-US` 의 `hour12: false` 는 자정을 `24` 로 쓸 수 있다).
 */
const TIME = localeDateTimeFormat({
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: DISPLAY_TIME_ZONE,
})

const TIME_SEC = localeDateTimeFormat({
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  timeZone: DISPLAY_TIME_ZONE,
})

const DATE_TIME = localeDateTimeFormat({
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: DISPLAY_TIME_ZONE,
})

function parse(value: string | null | undefined): Date | null {
  if (value == null || value === '') return null
  const ms = Date.parse(value)
  return Number.isNaN(ms) ? null : new Date(ms)
}

/** `14:31` — 같은 날 안에서 보는 값(알람 발생 시각 등) */
export function timeText(value: string | null | undefined): string {
  const date = parse(value)
  return date === null ? t('mask.none') : TIME().format(date)
}

/** `14:31:58` — 이벤트·명령 로그처럼 초가 의미 있는 값 */
export function timeSecText(value: string | null | undefined): string {
  const date = parse(value)
  return date === null ? t('mask.none') : TIME_SEC().format(date)
}

/**
 * `09-11 14:31` — 여러 날에 걸친 목록(이력 · 로그).
 * 로케일 포맷 문자열을 가공하지 않고 **조각으로 조립한다** — `ko-KR` 의 `09. 11.` 을 치환하면
 * 날짜와 시각 구분이 뭉개진다(`09-11-14:31`). 조각으로 지으면 언어가 바뀌어도 같은 꼴이다(`en-US` 의 `09/11`).
 */
export function dateTimeText(value: string | null | undefined): string {
  const date = parse(value)
  if (date === null) return t('mask.none')
  const parts = new Map(
    DATE_TIME()
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  )
  const month = parts.get('month') ?? ''
  const day = parts.get('day') ?? ''
  const hour = parts.get('hour') ?? ''
  const minute = parts.get('minute') ?? ''
  return `${month}-${day} ${hour}:${minute}`
}

const FULL_DATE_TIME = localeDateTimeFormat({
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  /* 🔴 `hour12: false` 는 엔진에 따라 자정을 `24` 로 쓴다 — 시 자리를 00~23 으로 못박는다 */
  hourCycle: 'h23',
  timeZone: DISPLAY_TIME_ZONE,
})

/**
 * `2026-09-30 13:05:42` — 연 · 월 · 일 · 시 · 분 · 초 전부(eric 2026-09-30 「년월일시초 다 보여주게」 · 알람 발생 시각).
 * `dateTimeText` 와 같이 **조각으로 조립한다**. 서버 값의 초 아래(마이크로초)는 버린다 — 반올림하지 않는다.
 */
export function fullDateTimeText(value: string | null | undefined): string {
  const date = parse(value)
  if (date === null) return t('mask.none')
  const parts = new Map(
    FULL_DATE_TIME()
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  )
  const at = (type: Intl.DateTimeFormatPartTypes) => parts.get(type) ?? ''
  return `${at('year')}-${at('month')}-${at('day')} ${at('hour')}:${at('minute')}:${at('second')}`
}

/** `9.4 h` — 시약 여유처럼 시간 길이를 보이는 값 */
export function hoursText(seconds: number | null | undefined): string {
  if (seconds == null) return t('mask.none')
  return `${(seconds / 3600).toFixed(1)} h`
}

/** `4.1 ℃` */
export function tempText(celsius: number | null | undefined): string {
  if (celsius == null) return t('mask.none')
  return `${celsius.toFixed(1)} ℃`
}

/** `1,021` — 큰 수는 자릿점을 찍는다(표는 tabular-nums 라 자리가 안 흔들린다) */
export function numberText(value: number | null | undefined): string {
  if (value == null) return t('mask.none')
  return value.toLocaleString(intlLocale())
}

/** 값이 없으면 `—`. 빈 문자열도 없는 것으로 본다 */
export function orDash(value: string | null | undefined): string {
  return value == null || value === '' ? t('mask.none') : value
}

/**
 * 알람 한 줄 제목. 🔴 **에러 코드 자체를 화면에 쓰지 않는다**(§1-1).
 * 사전에 덮어쓴 코드면 그 문구, 아니면 서버 문구, 둘 다 없으면 기본 문구.
 */
export function errorMessageOf(code: string | undefined, message: string | undefined): string {
  return errorMessage({ error_code: code, message })
}

/**
 * 알려진 장치 이름 사전. 계약이 주는 짧은 키(`cent`)와 인스턴스·계층 ID 의
 * 앞자리(`staining-1` · `module:cent`) 양쪽을 같은 표로 받는다.
 */
const MODULE_ALIAS: Record<string, ModuleId> = {
  cent: 'cent',
  centrifuge: 'cent',
  asm: 'asm',
  assembler: 'asm',
  stain: 'stain',
  staining: 'stain',
  robot: 'robot',
  // 실모듈 ID 앞 조각(`robot6-1` · 2026-10-23 로컬 실측) — 6축 운송 로봇이다
  robot6: 'robot',
  vision: 'vision',
  safety: 'safety',
  system: 'system',
  // 🔴 알람 `source` 실값이다(2026-09-18 실측). 표기는 「시스템」이다 — 「오케스트레이터」라고
  //    쓰지 않는다(§15 용어 · `enum.controlOwner.orchestrator` 가 이미 같은 낱말을 쓴다)
  orchestrator: 'system',
}

/**
 * 로그 `target` · 알람 `source` 처럼 **자유 문자열로 오는 발생처**를 화면 이름으로 바꾼다.
 *
 * 🔴 사전에 있으면 한글로, 없으면 **원값을 그대로 둔다** — `batch:B-260911-02` 나
 *    `l3.centrifuge/driver.sigma` 는 장치·자원 식별자라 번역 대상이 아니다.
 *    (enum 을 `—` 로 떨어뜨리는 `@ctcells/i18n` 의 `enumLabel` 과 규칙이 다른 이유다.)
 *
 * 알람·로그·작업 상세 셋이 각자 같은 표를 들고 있던 것을 여기 한 곳으로 모았다.
 */
export function moduleLabelOf(raw: string | null | undefined): string {
  if (raw == null || raw === '') return t('mask.none')
  const head = raw.split(/[:-]/)[0] ?? raw
  const mapped = MODULE_ALIAS[raw] ?? MODULE_ALIAS[head]
  return mapped === undefined ? raw : moduleLabel(mapped)
}

/**
 * 이력(`GET /history`) 한 줄의 **대상**. `HistoryEntry.target` 을 사람이 읽는 이름으로 바꾼다.
 *
 * 🔴 **접두어가 붙어 온다.** 서버가 `"module:"` · `"batch:"` 를 직접 이어 붙인다
 *    (`app/services/history.py:79-88` — `sa.literal("module:").concat(...)`). 그래서
 *    `moduleLabelOf()` 에 그대로 넘기면 앞 조각이 `"module"` 이라 별칭표에 없고
 *    **`module:cent` 가 화면에 그대로 샜다**(2026-09-20 실측 버그).
 *
 * 🔴 **모듈만 오는 자리가 아니다.** 배치 이벤트는 `batch:B-260911-02` 로 온다 — 그래서 이 열은
 *    「모듈」이 아니라 **「대상」**이다. 열 이름을 되돌리지 마라.
 * ⚠ 배치 ID 는 **번역 대상이 아니다.** 접두어만 떼고 식별자를 그대로 보인다.
 * ⚠ 아는 접두어가 없으면 `moduleLabelOf()` 에 맡긴다 — 알람 `source`(`l3.centrifuge/driver.sigma`)
 *   처럼 접두어 없이 오는 값이 이 함수로 흘러도 예전과 같이 동작하게 둔다.
 */
export function historyTargetText(raw: string | null | undefined): string {
  if (raw == null || raw === '') return t('mask.none')

  const colon = raw.indexOf(':')
  if (colon > 0) {
    const kind = raw.slice(0, colon)
    const id = raw.slice(colon + 1)
    if (id !== '') {
      if (kind === 'module') return moduleLabelOf(id)
      // 배치·시료 등 식별자는 그대로 보인다. 접두어는 사람에게 값을 더하지 않는다
      if (kind === 'batch' || kind === 'sample') return id
    }
  }
  return moduleLabelOf(raw)
}

/**
 * 알람 한 줄의 **발생처**(`AlarmEvent.source`) → 화면 표기.
 *
 * 🔴 **`source` 는 모듈 «종류» 축이지 모듈 «ID» 가 아니다.** 실값은 넷이다(2026-09-18 실측):
 *    `module.centrifuge` · `orchestrator` · `*` · `probe-module`. 앞의 것은 **`module.` +
 *    `module_type`** 이라 `moduleTypeLabel()` 이 그대로 읽는다 — 종전에는 `moduleLabelOf()` 가
 *    점(`.`)을 끊지 못해 **`module.centrifuge` 가 화면에 그대로 샜다.**
 *
 * 🔴 **`*` 는 「어느 모듈에서든 날 수 있는 코드」다 — 모듈 이름이 아니다.** 지어낸 말로 바꾸지
 *    않고 원값으로 둔다. `—` 로도 접지 않는다 — `—` 는 「서버가 안 줬다」 전용이고 이건 받은 값이다.
 *
 * 🔴 **어느 «대»에서 났는지는 이 칸이 말하지 못한다.** 모듈 인스턴스 ID 는 `grouping_key` 의
 *    뒤 조각(`MQTT_DISCONNECT@centrifuge-1`)에만 있고 서버도 그 꼴로 센다
 *    (`app/services/alarms.py` `counts_by_module` 가 `rpartition('@')` 한다). 다만 그 꼴은
 *    **발행하는 쪽이 정하는 자유 문자열**이라(`raise_alarm` 주석 — 「창구가 지어내지 않는다」)
 *    화면이 갈라 읽으면 틀린 묶음을 사람 이름처럼 보이게 된다. → **팀장 보고 대상**이다.
 */
export function alarmSourceText(raw: string | null | undefined): string {
  if (raw == null || raw === '') return t('mask.none')
  const dot = raw.indexOf('.')
  if (dot > 0 && raw.slice(0, dot) === 'module') {
    const moduleType = raw.slice(dot + 1)
    // 모르는 종류(`ceiling_3axis`)는 원값 그대로 — 지어내지 않는다
    return moduleTypeLabel(moduleType) ?? raw
  }
  return moduleLabelOf(raw)
}

/**
 * 두 시각 사이의 **소요 시간** `08:11:34`. 하나라도 없으면 `—`.
 *
 * 🔴 **계약에 소요 필드가 없다.** `Batch` 는 `started_at` · `completed_at` 을 줄 뿐이라
 *    화면이 «뺄셈»으로 만든다. 지어내는 값이 아니라 **응답 두 칸에서 결정되는 값**이다.
 * ⚠ 2026-09-20 이전에는 이력 「기간」 열이 **시작 «시각»을 그리고 있었다**(라벨과 값이 달랐다).
 *   되돌리지 마라.
 * ⚠ 진행 중 배치에는 쓰지 않는다 — `completed_at` 이 없으면 `—` 다. 지금 시각으로 빼면
 *   화면이 시계를 돌리는 것이 되고, 경과는 구독(`batch_progress`)이 주는 값이다.
 */
export function durationText(
  from: string | null | undefined,
  to: string | null | undefined,
): string {
  const start = parse(from)
  const end = parse(to)
  if (start === null || end === null) return t('mask.none')

  const seconds = Math.floor((end.getTime() - start.getTime()) / 1000)
  // 음수는 시계가 어긋난 것이다 — 「-00:01:00」 같은 값을 보이느니 모른다고 한다
  if (seconds < 0) return t('mask.none')

  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`
}

/**
 * 낡음(stale) 표지의 덧말 — 「마지막 응답 09-19 10:24」. 서버가 준 `last_seen` 이 없으면 `undefined`
 * (표지만 선다 — 시각을 지어내지 않는다).
 *
 * 🔴 정본 07(2026-09-25): stale = 낡음 표기(투명도 아님) + 「응답 없음」 + **마지막 값 병기**.
 */
export function staleDetail(lastSeen: string | null | undefined): string | undefined {
  if (lastSeen == null || lastSeen === '') return undefined
  const time = dateTimeText(lastSeen)
  return time === t('mask.none') ? undefined : t('stale.lastSeen', { time })
}

/**
 * 낡은 스냅샷의 값 표기 — 「대기 · 마지막 값」. 🔴 **마지막 값을 «지금»으로 보이지 않는다.**
 * 낡지 않았으면 그대로다. 값이 없으면(`—`) 덧붙이지 않는다 — 「— · 마지막 값」은 말이 안 된다.
 */
export function staleValue(value: string, stale: boolean): string {
  if (!stale || value === t('mask.none')) return value
  return t('stale.lastValue', { value })
}
